/**
 * How an apply ended, as a strip across the top of the selected template's
 * card: the same place the apply overlay showed its progress. Built by
 * lib/applyResult, or by lib/subscriptionVerdict when the apply was refused
 * before it started; dismissed with ✕, or replaced by the next apply.
 *
 * A result with `usage: true` (the operations limit) also offers "View app
 * usage", which opens the usage view through `onUsage`.
 */
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import React from 'react';
import { failureLabel } from '../lib/applyResult';

const TONES = {
  success: { box: 'bg-positive-tint', icon: CheckCircle2, ink: 'text-positive' },
  warning: { box: 'bg-warning-tint', icon: AlertTriangle, ink: 'text-warning-ink' },
  danger: { box: 'bg-danger-tint', icon: XCircle, ink: 'text-danger-ink' },
};

export default function ApplyResultAlert({ result, onClose, onUsage }) {
  if (!result) return null;
  const tone = TONES[result.tone] || TONES.success;
  const Icon = tone.icon;
  const failures = result.failures || [];
  return (
    <div
      className={`-mx-6 -mt-6 px-6 py-4 rounded-t-lg border-b border-subtle ${tone.box}`}
      role={result.tone === 'success' ? 'status' : 'alert'}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 flex-none ${tone.ink}`} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-primary">{result.title}</div>
          {result.summary && <p className="mt-0.5 text-sm text-primary">{result.summary}</p>}
          {failures.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-sm text-primary max-h-40 overflow-y-auto">
              {failures.map((f, i) => (
                <li key={i}>
                  <span className="font-medium">{failureLabel(f)}</span>: {f.reason}
                </li>
              ))}
            </ul>
          )}
          {result.notice && <p className="mt-2 text-sm text-primary">{result.notice}</p>}
          {result.note && <p className="mt-2 text-xs text-secondary">{result.note}</p>}
          {result.usage && onUsage && (
            <button
              type="button"
              onClick={onUsage}
              className="mt-2 text-sm font-medium underline text-primary hover:text-secondary"
            >
              View app usage
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="flex-none p-1 -m-1 rounded-md text-secondary hover:text-primary hover:bg-hover"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
